import { ChangeEvent, ReactNode, useEffect, useState } from 'react';

import {
  Alert,
  AngleDown,
  AngleUp,
  ChakraBox,
  ChakraCollapse,
  ChakraFlex,
  ChakraGridItem,
  ChakraHStack,
  ChakraSimpleGrid,
  ChakraText,
  ChakraVStack,
  Check,
  CircularProgress,
  Edit,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Note,
  TextInput,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';

import StatusLabel from '~/components/maud/tasks/TaskList/TaskListItem/StatusLabel';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { useLazyGetObservationHistoryQuery } from '~/store/services/clinicalData';

import ActionButton from './ActionButton';
import DatetimeGridItem from './DatetimeGridItem';
import GoalStatusGridItem from './GoalStatusGridItem';
import HistoryItem from './HistoryItem';
import MultiValueGridItem from './MultiValueGridItem';
import PositionGridItem from './PositionGridItem';
import {
  EditStatusColSpan,
  GoalStatusColSpan,
  MeasuredAtColSpan,
  getNumColumns,
  getObservationBasedOnType,
  getProperDateString,
  getUniqueItemsByProperties,
  getValueColSpan,
  isBloodPressureHeartRateTab,
  shouldShowClinicalProfilePositionTab,
} from './utils';
import ValueGridItem from './ValueGridItem';
import {
  BLOOD_PRESSURE,
  HEART_RATE,
  ObservationHistoryField,
  ObservationStatusType,
  ParsedActivityEvent,
  PatientVitalsTabs,
  PatientVitalsTabsCollection,
  keyVitalsItemStyles,
} from '../../constants';

const { label: labelStyle, paragraph } = EquipUITheme.typography;
const { critical, neutral, primary } = EquipUIFireflyV2Theme.colors;

const activityChangesToLog = Object.fromEntries(
  Object.values(ObservationHistoryField).map((field) => [field, true]),
);

const GridItem = ({
  children,
  colSpan,
}: {
  children: ReactNode;
  colSpan?: number;
}) => (
  <ChakraGridItem alignItems="center" colSpan={colSpan}>
    {children}
  </ChakraGridItem>
);

type KeyVitalsItemProps = {
  handleVoidClick: (
    observations: PatientVitalsObservation | PatientVitalsObservation[],
  ) => void;
  isUpdateObservationProcessing: boolean;
  observations: PatientVitalsObservation | PatientVitalsObservation[];
  tab: PatientVitalsTabs;
  updateObservation: (options: PatientVitalsObservation) => void;
  isSuccessObservationProcessing: boolean;
  updateObservationError: boolean;
};

type ObservationType = PatientVitalsObservation & {
  tertiaryValue: number;
  tertiaryUnit: string;
};

// Handles single/multiple observation (single entry) and returns consolidated observation
const amalgamateObservation = (
  observations: PatientVitalsObservation | PatientVitalsObservation[],
): ObservationType => {
  let processedObservation: ObservationType = null;

  if (Array.isArray(observations)) {
    const primaryObservation = observations[0];
    if (primaryObservation) {
      if (primaryObservation.type === BLOOD_PRESSURE) {
        processedObservation = {
          ...primaryObservation,
          tertiaryUnit: null,
          tertiaryValue: null,
        };
      } else if (primaryObservation.type === HEART_RATE) {
        processedObservation = {
          ...primaryObservation,
          secondaryValue: null,
          tertiaryUnit: primaryObservation.unit,
          tertiaryValue: primaryObservation.value,
          unit: null,
          value: null,
        };
      }
    }

    const secondaryObservation = observations[1];
    if (secondaryObservation) {
      if (secondaryObservation.type === BLOOD_PRESSURE) {
        if (isEmpty(processedObservation)) {
          processedObservation = {
            ...secondaryObservation,
            tertiaryUnit: null,
            tertiaryValue: null,
          };
        } else {
          processedObservation = {
            ...processedObservation,
            ...(processedObservation.note !== secondaryObservation.note && {
              note: `${processedObservation.note}(${processedObservation.type}) / ${secondaryObservation.note}(${secondaryObservation.type})`,
            }),
            tertiaryUnit: null,
            tertiaryValue: null,
          };
        }
      } else if (secondaryObservation.type === HEART_RATE) {
        if (isEmpty(processedObservation)) {
          processedObservation = {
            ...secondaryObservation,
            secondaryValue: null,
            tertiaryUnit: secondaryObservation.unit,
            tertiaryValue: secondaryObservation.value,
            unit: null,
            value: null,
          };
        } else {
          processedObservation = {
            ...processedObservation,
            ...(processedObservation.note !== secondaryObservation.note && {
              note: `${processedObservation.note}(${processedObservation.type}) / ${secondaryObservation.note}(${secondaryObservation.type})`,
            }),
            tertiaryUnit: secondaryObservation.unit,
            tertiaryValue: secondaryObservation.value,
          };
        }
      }
    }
  } else {
    // Single observation
    processedObservation = {
      ...observations,
      secondaryValue: null,
      tertiaryUnit: null,
      tertiaryValue: null,
    };
  }

  return processedObservation;
};

const KeyVitalsItem = ({
  handleVoidClick,
  isUpdateObservationProcessing,
  observations,
  tab,
  updateObservation,
  updateObservationError,
  isSuccessObservationProcessing,
}: KeyVitalsItemProps) => {
  const {
    interpretation,
    note,
    observationDatetime,
    observationExternalId,
    position,
    secondaryValue,
    source,
    status,
    tertiaryUnit,
    tertiaryValue,
    unit,
    value,
  } = { ...amalgamateObservation(observations) };

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const handleToggle = (): void => setIsExpanded(!isExpanded);
  const [historyInitiallyFetched, setHistoryInitiallyFetched] =
    useState<boolean>(false);
  const [parsedHistoryData, setParsedHistoryData] = useState<
    ParsedActivityEvent[]
  >([]);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  // number input actually returns strings in its on change handler, which is necessary to handle values such as "10." when typing 10.5 for example
  // so, these are best kept as strings until sending data to API
  const [newValue, setNewValue] = useState<string>(value?.toString(10) || '');
  const [newSecondaryValue, setNewSecondaryValue] = useState<string>(
    secondaryValue?.toString(10) || '',
  );
  const [noteValue, setNoteValue] = useState<string>(note);
  const [newTertiaryValue, setNewTertiaryValue] = useState<string>(
    tertiaryValue?.toString(10) || '',
  );
  const [newDatetime, setNewDatetime] = useState<string>(observationDatetime);
  const [newInterpretation, setNewInterpretation] =
    useState<string>(interpretation);
  const [newPosition, setNewPosition] = useState<string>(position);
  const [getHistory, { data: historyData, isLoading: isHistoryLoading }] =
    useLazyGetObservationHistoryQuery();
  const TOP_LEVEL_ID = `patient__key-vitals-list-item__${observationExternalId}`;
  const DETAILS_VIEW_ID = `${TOP_LEVEL_ID}__details-view`;
  const HISTORY_ID = `${TOP_LEVEL_ID}__history`;

  const getUpdatedObservationOnType = (
    observation: PatientVitalsObservation,
  ): PatientVitalsObservation => {
    const updatedObservation: PatientVitalsObservation = { ...observation };
    if (updatedObservation.type === BLOOD_PRESSURE) {
      updatedObservation.value = Number(newValue);
      updatedObservation.secondaryValue = Number(newSecondaryValue);
    } else if (updatedObservation.type === HEART_RATE) {
      updatedObservation.value = Number(newTertiaryValue);
    } else {
      return null;
    }
    return updatedObservation;
  };

  const handleSaveEdit = () => {
    const commonOptions = {
      interpretation: newInterpretation,
      note: noteValue,
      observationDatetime: newDatetime,
      status: ObservationStatusType.CORRECTED,
    };

    const updatedObservations: PatientVitalsObservation[] = [];

    if (isBloodPressureHeartRateTab(tab)) {
      if (Array.isArray(observations)) {
        observations.forEach((obs) => {
          const updatedObservation = getUpdatedObservationOnType({
            ...obs,
            ...commonOptions,
          });
          if (updatedObservation) updatedObservations.push(updatedObservation);
        });
      } else {
        const updatedObservation = getUpdatedObservationOnType({
          ...observations,
          ...commonOptions,
        });

        if (updatedObservation) updatedObservations.push(updatedObservation);

        // If the second field is a newly entered value
        if (tertiaryValue > 0) {
          // Heart rate (newly added)
          const newObservationOption: PatientVitalsObservation = {
            ...commonOptions,
            createdAt: null,
            createdBy: null,
            lastUpdatedAt: null,
            observationExternalId: null,
            patientExternalId: null,
            source: 'Manual',
            status: ObservationStatusType.FINAL,
            type: HEART_RATE,
            unit: PatientVitalsTabsCollection[tab].unit[1],
            value: tertiaryValue,
          };

          if (newObservationOption) {
            updatedObservations.push(newObservationOption);
          }
        }
      }
    } else {
      updatedObservations.push({
        ...(observations as PatientVitalsObservation),
        ...commonOptions,
        value: Number(newValue),
      });
    }

    // Save single/multiple observations
    updatedObservations.forEach((updatedObservation) => {
      if (shouldShowClinicalProfilePositionTab(tab)) {
        updatedObservation.position = newPosition;
      }
      updateObservation(updatedObservation);
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewValue(value.toString(10));
    setNewDatetime(observationDatetime);
    setNewInterpretation(interpretation);
  };

  useEffect(() => {
    if (isSuccessObservationProcessing) {
      setIsEditing(false);
    }
  }, [isSuccessObservationProcessing]);

  useEffect(() => {
    if (isExpanded && !historyInitiallyFetched) {
      if (Array.isArray(observations)) {
        observations.forEach((obs) => {
          if (obs?.observationExternalId) {
            getHistory(obs.observationExternalId);
          }
        });
      } else {
        getHistory(observationExternalId);
      }
      setHistoryInitiallyFetched(true);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!historyData?.length) return;
    const changes: ParsedActivityEvent[] = [];
    historyData.forEach(
      (
        activity: PatientVitalsObservationHistory,
        i: number,
        activites: PatientVitalsObservationHistory[],
      ) => {
        if (i === activites.length - 1) return; // handle creation separately
        const prevActivity = activites[i + 1];
        const changedFields = Object.keys(activity).filter(
          (field: ObservationHistoryField) =>
            activityChangesToLog[field] &&
            activity[field] !== prevActivity[field],
        );
        changedFields.forEach((field: ObservationHistoryField) => {
          // hide change to status edited since the specific edit will already show up in the log
          if (
            field === ObservationHistoryField.STATUS &&
            activity[field] === ObservationStatusType.CORRECTED
          ) {
            return;
          }
          changes.push({
            activityExternalId: activity.activityExternalId,
            fieldChanged: field,
            newValue: activity[field],
            previousValue: prevActivity[field],
            type: activity.type,
            updatedAt: activity.updatedAt,
            updatedBy: activity.updatedByUserName,
          });
        });
      },
    );
    // handle creation event
    const creationEvent = historyData[historyData.length - 1];
    const observationBasedOnCreationEvent = getObservationBasedOnType(
      observations,
      creationEvent.type,
    );

    changes.push({
      activityExternalId: creationEvent.activityExternalId,
      fieldChanged: ObservationHistoryField.CREATED,
      newValue: '',
      type: creationEvent.type,
      updatedAt: creationEvent.updatedAt,
      updatedBy:
        getPreferredFullName(
          null,
          observationBasedOnCreationEvent?.createdByFirstName,
          observationBasedOnCreationEvent?.createdByLastName,
        ) ?? creationEvent.updatedByUserName,
    });

    setParsedHistoryData((prevValue) => [...prevValue, ...changes]);
  }, [historyData?.length, isSuccessObservationProcessing]);

  let statusDisplay: ReactNode = null;
  if (status === ObservationStatusType.CORRECTED) {
    statusDisplay = (
      <ChakraText
        {...labelStyle}
        color="neutral.secondary"
        id={`${TOP_LEVEL_ID}__status`}
      >
        (Edited)
      </ChakraText>
    );
  } else if (status === ObservationStatusType.VOID) {
    statusDisplay = (
      <StatusLabel
        color="red.10"
        status={ObservationStatusType.VOID}
        textColor="critical.base"
      />
    );
  }

  const ToggleIcon = isExpanded ? AngleUp : AngleDown;

  return (
    <ChakraBox
      {...keyVitalsItemStyles.container}
      id={TOP_LEVEL_ID}
      key={TOP_LEVEL_ID}
    >
      <ChakraSimpleGrid
        {...keyVitalsItemStyles.mainGrid}
        templateColumns={
          isBloodPressureHeartRateTab(tab)
            ? '15% 8% 25% 12% 8% 10% auto'
            : `repeat(${getNumColumns(tab)}, 1fr)`
        }
      >
        {isBloodPressureHeartRateTab(tab) === false ? (
          <ValueGridItem<string>
            colSpan={getValueColSpan(tab)}
            id={`${TOP_LEVEL_ID}__single-value`}
            isEditing={isEditing}
            onChange={(val) => setNewValue(val)}
            tab={tab}
            unit={unit}
            value={newValue}
          />
        ) : (
          <>
            <MultiValueGridItem
              colSpan={getValueColSpan(tab)}
              id={`${TOP_LEVEL_ID}__multi-value`}
              isEditing={isEditing}
              onChange={(vals) => {
                setNewValue(vals[0].replaceAll('.', ''));
                setNewSecondaryValue(vals[1].replaceAll('.', ''));
              }}
              unit={unit}
              value={[newValue, newSecondaryValue]}
            />
            <ValueGridItem<string>
              colSpan={getValueColSpan(tab)}
              id={`${TOP_LEVEL_ID}__tertiary-value`}
              isEditing={isEditing}
              onChange={(val) => setNewTertiaryValue(val.replaceAll('.', ''))}
              tab={tab}
              unit={tertiaryUnit}
              value={newTertiaryValue}
            />
          </>
        )}
        <DatetimeGridItem
          colSpan={isBloodPressureHeartRateTab(tab) ? 1 : MeasuredAtColSpan}
          id={`${TOP_LEVEL_ID}__measured-at`}
          isEditing={isEditing}
          onChange={(val: Date) => {
            setNewDatetime(val.toISOString());
          }}
          value={new Date(newDatetime)}
        />
        {shouldShowClinicalProfilePositionTab(tab) && (
          <PositionGridItem
            colSpan={getValueColSpan(tab)}
            id={`${TOP_LEVEL_ID}__position`}
            isEditing={isEditing}
            onChange={(val: string) => setNewPosition(val)}
            value={newPosition}
          />
        )}
        <GridItem colSpan={getValueColSpan(tab)}>
          <ChakraText {...paragraph} id={`${TOP_LEVEL_ID}__source`}>
            {source}
          </ChakraText>
        </GridItem>
        <GoalStatusGridItem
          colSpan={isBloodPressureHeartRateTab(tab) ? 1 : GoalStatusColSpan}
          id={`${TOP_LEVEL_ID}__goal-status`}
          isEditing={isEditing}
          onChange={(val: string) => setNewInterpretation(val)}
          value={newInterpretation}
        />
        <GridItem
          colSpan={isBloodPressureHeartRateTab(tab) ? 1 : EditStatusColSpan}
        >
          <ChakraFlex justify="flex-end">
            <ChakraFlex
              alignItems="center"
              id={`${DETAILS_VIEW_ID}__status`}
              justifyContent="space-between"
            >
              {statusDisplay}
              {noteValue && (
                <ChakraBox
                  id={`${DETAILS_VIEW_ID}__note-icon`}
                  paddingLeft="5px"
                >
                  <Note height="16px" width="13px" />
                </ChakraBox>
              )}
            </ChakraFlex>
            <ChakraFlex
              alignItems="center"
              cursor="pointer"
              id={`${DETAILS_VIEW_ID}__toggle`}
              marginLeft="24px"
              onClick={handleToggle}
            >
              <ToggleIcon
                fill={neutral.secondary}
                height="8.4px"
                width="100%"
              />
            </ChakraFlex>
          </ChakraFlex>
        </GridItem>
      </ChakraSimpleGrid>
      <ChakraCollapse in={isExpanded}>
        <ChakraSimpleGrid gap="24px" id={DETAILS_VIEW_ID} paddingTop="16px">
          <GridItem>
            {(isEditing || note) && (
              <ChakraText {...labelStyle} id={`${TOP_LEVEL_ID}__note-label`}>
                Note
              </ChakraText>
            )}

            {isEditing ? (
              <TextInput
                color="neutral.primary"
                formMarginBottom="10px"
                height="32px"
                id={`${TOP_LEVEL_ID}__note-input`}
                isRequired
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setNoteValue(event.target.value);
                }}
                size="sm"
                value={noteValue}
              />
            ) : (
              note && (
                <ChakraText {...paragraph} paddingLeft="16px">
                  {note}
                </ChakraText>
              )
            )}
          </GridItem>
          <GridItem>
            <ChakraHStack
              justify={isEditing ? 'flex-end' : 'flex-start'}
              spacing="8px"
            >
              {isEditing ? (
                <>
                  <ActionButton
                    id={`${TOP_LEVEL_ID}__edit-discard`}
                    isDisabled={isUpdateObservationProcessing}
                    onClick={handleCancelEdit}
                    title="Discard"
                  />
                  <ActionButton
                    icon={
                      <Check
                        fill={neutral.background.primary}
                        height="16px"
                        width="16px"
                      />
                    }
                    id={`${TOP_LEVEL_ID}__edit-save`}
                    isDisabled={isUpdateObservationProcessing}
                    onClick={handleSaveEdit}
                    title="Save changes"
                    variant="solid"
                  />
                </>
              ) : (
                <>
                  <ActionButton
                    icon={
                      <Edit fill={primary[100]} height="16px" width="16px" />
                    }
                    id={`${TOP_LEVEL_ID}__edit`}
                    isDisabled={status === ObservationStatusType.VOID}
                    onClick={() => setIsEditing(true)}
                    title="Edit"
                  />
                  <ActionButton
                    icon={
                      <Alert fill={critical.base} height="16px" width="16px" />
                    }
                    id={`${TOP_LEVEL_ID}__void`}
                    isDisabled={status === ObservationStatusType.VOID}
                    onClick={() => handleVoidClick(observations)}
                    title="Void value"
                  />
                </>
              )}
            </ChakraHStack>
            {updateObservationError && isEditing && (
              <ChakraText
                {...labelStyle}
                color="critical.base"
                textAlign="right"
              >
                Error updating observation
              </ChakraText>
            )}
          </GridItem>
          <GridItem>
            <ChakraText
              {...labelStyle}
              color="neutral.secondary"
              id={`${HISTORY_ID}-section-label`}
              paddingBottom="8px"
            >
              Activity
            </ChakraText>
            {isHistoryLoading ? (
              <CircularProgress
                id={`${HISTORY_ID}-loading`}
                isIndeterminate
                size="20px"
              />
            ) : (
              <ChakraVStack align="flex-start" spacing="8px">
                {getUniqueItemsByProperties(
                  parsedHistoryData.map((phisValue) => {
                    return {
                      ...phisValue,
                      updatedAt: getProperDateString(phisValue.updatedAt),
                    };
                  }),
                  [
                    'fieldChanged',
                    'newValue',
                    'previousValue',
                    'updatedAt',
                    'updatedBy',
                  ],
                )
                  ?.sort((a: ParsedActivityEvent, b: ParsedActivityEvent) =>
                    b.updatedAt.localeCompare(a.updatedAt),
                  )
                  .map((activity: ParsedActivityEvent) => {
                    const id = `${HISTORY_ID}__${activity.activityExternalId}__${activity.fieldChanged}`;
                    return (
                      <HistoryItem
                        historyEvent={activity}
                        id={id}
                        key={id}
                        tab={tab}
                      />
                    );
                  })}
              </ChakraVStack>
            )}
          </GridItem>
        </ChakraSimpleGrid>
      </ChakraCollapse>
    </ChakraBox>
  );
};

export default KeyVitalsItem;
